import { useCallback, useEffect, useRef, useState } from 'react';
import { RotateCcw } from 'lucide-react';
import { SelectField } from '@/components/formField/selectField';
import { getDistricts, getNeighbourhoods, getProvinces } from '@/services/locationService';
import type { LocationNode } from '@/types/domain';
import { tr } from '@/i18n/tr';
import { selectAtLevel, type LocationSelection } from './locationSelection';
import {
  applyLevelFailure,
  applyLevelSuccess,
  initialLevelLoadState,
  startLevelLoad,
} from './levelLoad';
import styles from './locationCascade.module.scss';

export type { LocationSelection } from './locationSelection';

export interface LocationCascadeProps {
  value: LocationSelection;
  onChange: (next: LocationSelection) => void;
  /** Marks the mahalle select required. The Explore filter leaves it optional. */
  requireNeighbourhood?: boolean;
  requiredMark?: boolean;
}

/**
 * Loads one level of the cascade, keyed by its parent's id (or a constant key
 * for the root province list, which has no parent).
 *
 * Guards against two kinds of stale response — the parent changing again
 * before this fetch resolves, and a manual retry firing before the previous
 * attempt has settled — using the generation-counter logic in `./levelLoad`.
 * Only the most recent request for this key is allowed to write into state,
 * so a slow response for a province the user has already left behind can
 * never overwrite the list for the one they are on now.
 *
 * A failed fetch is tracked separately from an empty result — `failed` — so
 * the caller can tell "nothing here" apart from "something went wrong" and
 * offer a retry, which the underlying service supports because it evicts a
 * failed attempt from its cache as soon as it rejects.
 */
function useLocationLevel<K>(
  parentKey: K | null,
  fetchLevel: (key: K) => Promise<LocationNode[]>
) {
  const [state, setState] = useState(() => initialLevelLoadState<LocationNode>());
  const fetchRef = useRef(fetchLevel);
  fetchRef.current = fetchLevel;

  const load = useCallback((key: K | null) => {
    setState((prev) => {
      const { state: next, generation } = startLevelLoad(prev, key != null);
      if (key != null) {
        fetchRef.current(key)
          .then((rows) => setState((s) => applyLevelSuccess(s, generation, rows)))
          .catch(() => setState((s) => applyLevelFailure(s, generation)));
      }
      return next;
    });
  }, []);

  useEffect(() => {
    load(parentKey);
  }, [parentKey, load]);

  return { items: state.items, failed: state.failed, retry: () => load(parentKey) };
}

function LevelError({ onRetry }: { onRetry: () => void }) {
  return (
    <p className={styles.levelError}>
      {tr.location.loadError}
      <button type="button" className={styles.retryButton} onClick={onRetry}>
        <RotateCcw aria-hidden /> {tr.location.retry}
      </button>
    </p>
  );
}

/**
 * Province, district and neighbourhood chosen one level at a time, each list
 * loaded only once its parent is known. Selecting any level — or clearing one
 * back to its placeholder — clears the levels below it via `selectAtLevel`,
 * so a stale district can never survive a change of province.
 *
 * `bbox` on the reported selection is always the extent of the deepest chosen
 * level, which is what a map should frame.
 *
 * A level's list carries `LocationNode.id`, but that id means something
 * different at each depth: for a province it is the plate code `getDistricts`
 * expects, for a district it is the row id `getNeighbourhoods` expects. The
 * lookups below rely on that distinction — get it backwards and the wrong
 * list loads without any error.
 */
export function LocationCascade({
  value,
  onChange,
  requireNeighbourhood = false,
  requiredMark = false,
}: LocationCascadeProps) {
  const provinceLevel = useLocationLevel(true, () => getProvinces());
  const districtLevel = useLocationLevel(value.countyCode ?? null, getDistricts);
  const neighbourhoodLevel = useLocationLevel(value.townId ?? null, getNeighbourhoods);

  const currentProvince = provinceLevel.items.find((p) => p.id === value.countyCode);
  const currentDistrict = districtLevel.items.find((d) => d.id === value.townId);

  const handleProvinceChange = (id: string) => {
    const node = provinceLevel.items.find((p) => String(p.id) === id);
    onChange(selectAtLevel({}, 'province', node));
  };

  const handleDistrictChange = (id: string) => {
    const node = districtLevel.items.find((d) => String(d.id) === id);
    onChange(selectAtLevel({ province: currentProvince }, 'district', node));
  };

  const handleNeighbourhoodChange = (id: string) => {
    const node = neighbourhoodLevel.items.find((n) => String(n.id) === id);
    onChange(
      selectAtLevel({ province: currentProvince, district: currentDistrict }, 'neighbourhood', node)
    );
  };

  return (
    <div className={styles.cascade}>
      <div className={styles.field}>
        <SelectField
          label={tr.location.city}
          value={value.countyCode == null ? '' : String(value.countyCode)}
          onChange={(e) => handleProvinceChange(e.target.value)}
          required
          requiredMark={requiredMark}
        >
          <option value="">{tr.location.cityPlaceholder}</option>
          {provinceLevel.items.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </SelectField>
        {provinceLevel.failed && <LevelError onRetry={provinceLevel.retry} />}
      </div>

      <div className={styles.field}>
        <SelectField
          label={tr.location.district}
          value={value.townId == null ? '' : String(value.townId)}
          onChange={(e) => handleDistrictChange(e.target.value)}
          disabled={value.countyCode == null}
          required
          requiredMark={requiredMark}
        >
          <option value="">{tr.location.districtPlaceholder}</option>
          {districtLevel.items.map((d) => (
            <option key={d.id} value={d.id}>
              {d.name}
            </option>
          ))}
        </SelectField>
        {districtLevel.failed && <LevelError onRetry={districtLevel.retry} />}
      </div>

      <div className={styles.field}>
        <SelectField
          label={tr.location.neighbourhood}
          value={value.neighbourhoodId == null ? '' : String(value.neighbourhoodId)}
          onChange={(e) => handleNeighbourhoodChange(e.target.value)}
          disabled={value.townId == null}
          required={requireNeighbourhood}
          requiredMark={requiredMark && requireNeighbourhood}
        >
          <option value="">{tr.location.neighbourhoodPlaceholder}</option>
          {neighbourhoodLevel.items.map((n) => (
            <option key={n.id} value={n.id}>
              {n.name}
            </option>
          ))}
        </SelectField>
        {neighbourhoodLevel.failed && <LevelError onRetry={neighbourhoodLevel.retry} />}
      </div>
    </div>
  );
}
